import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	createReport,
	deleteReport,
	fetchReportById,
	fetchReportsMetaData,
	pinReport,
	updateReportById,
	fetchReportData,
	exportReportAsCSV,
	exportReportAsPDF,
} from './thunks';
import {
	ReportingState,
	UnifiedReportingResponse,
	ChartType,
	ChartPeriod,
	ReportConfig,
	ReportsMetaDataResponse,
	ActiveReport,
} from 'src/types/reportingTypes';
import {
	getValidChartType,
	transformReportsMetaDataPayload,
} from 'src/routes/unifiedreporting/helper';

const initialState: ReportingState = {
	tableData: null,
	chartData: null,
	loading: false,
	initialLoading: true,
	chartDataReceived: false,
	error: null,
	activeGraph: ChartType.LINE,
	activeReport: {
		reportId: '',
		name: '',
		type: '',
	},
	reportsMetaData: null,
	pinnedReport: null,
	activeChartConfig: {
		chartType: ChartType.LINE,
		chartPeriod: ChartPeriod.DAILY,
		searchQuery: '',
		relativeDateRange: 'LAST_7_DAYS',
		absoluteDateRangeStart: null,
		absoluteDateRangeEnd: null,
		topDisplayCount: 5,
		groupBy: [],
		sortBy: [],
		filters: [],
		hiddenColumns: [],
	},
	page: 1,
	pageSize: 25,
	toast: {
		message: '',
		type: 'success',
	},
};

const reportingSlice = createSlice({
	name: 'reporting',
	initialState,
	reducers: {
		resetReportingState: () => initialState,
		setGraphType: (state, action: PayloadAction<ChartType>) => {
			state.activeGraph = action.payload;
		},
		updateChartConfig: (
			state,
			action: PayloadAction<Partial<ReportConfig>>,
		) => {
			state.activeChartConfig = {
				...state.activeChartConfig,
				...action.payload,
			};
		},
		updatePage: (state, action: PayloadAction<number>) => {
			state.page = action.payload;
		},
		updatePageSize: (state, action: PayloadAction<number>) => {
			state.pageSize = action.payload;
		},
		updateActiveReport: (
			state,
			action: PayloadAction<ActiveReport | null>,
		) => {
			state.activeReport = action.payload;
		},
		pushToast: (
			state,
			action: PayloadAction<{
				message: string;
				type: 'info' | 'success' | 'error' | 'warning' | 'default';
			}>,
		) => {
			state.toast = { ...state.toast, ...action.payload };
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchReportData.pending, (state) => {
				state.loading = true;
				state.error = null;
				state.chartDataReceived = false;
			})
			.addCase(
				fetchReportData.fulfilled,
				(state, action: PayloadAction<UnifiedReportingResponse>) => {
					state.loading = false;
					state.error = null;
					state.tableData = action.payload.table;
					state.chartData = action.payload.chart;
					state.chartDataReceived = true;
				},
			)
			.addCase(fetchReportData.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
				state.chartDataReceived = true;
			})
			.addCase(fetchReportsMetaData.pending, (state) => {
				state.loading = true;
				// Only show initial loading for first-time load
				if (state.reportsMetaData === null) {
					state.initialLoading = true;
				}
				state.error = null;
			})
			.addCase(
				fetchReportsMetaData.fulfilled,
				(state, action: PayloadAction<ReportsMetaDataResponse>) => {
					state.loading = false;
					// Only update initialLoading if it's currently true
					if (state.initialLoading) {
						state.initialLoading = false;
					}
					state.error = null;
					const transformedPayload = transformReportsMetaDataPayload(
						action.payload,
					);
					state.reportsMetaData = transformedPayload;

					const reports = transformedPayload.reports?.reports || [];
					const pinnedReportId =
						transformedPayload.reports?.pinnedReport;

					let selectedReport = null;

					if (pinnedReportId) {
						selectedReport =
							reports.find(
								(r) => r.reportId === pinnedReportId,
							) || null;
					}

					// If no pinned report, pick the first Barracuda report
					if (!selectedReport) {
						selectedReport =
							reports.find((r) => r.type === 'Barracuda') || null;
					}

					if (selectedReport && state.activeReport.reportId === '') {
						state.activeReport.reportId = selectedReport.reportId;
						state.activeReport.name = selectedReport.name;
						state.activeReport.type = selectedReport.type;
						state.pinnedReport = selectedReport.reportId;
						state.activeChartConfig = {
							...state.activeChartConfig,
							...selectedReport.config,
						};
					}
				},
			)
			.addCase(fetchReportsMetaData.rejected, (state, action) => {
				state.loading = false;
				// Only update initialLoading if it's currently true
				if (state.initialLoading) {
					state.initialLoading = false;
				}
				state.error = action.payload as string;
			})
			.addCase(createReport.pending, (state) => {
				state.error = null;
			})
			.addCase(createReport.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(pinReport.fulfilled, (state, action) => {
				state.error = null;
				state.pinnedReport = action.payload;
			})
			.addCase(pinReport.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
				state.pinnedReport = state.activeReport.reportId;
			})
			.addCase(fetchReportById.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchReportById.fulfilled, (state, action) => {
				state.error = null;
				state.activeReport.reportId = action.payload.data.id;
				state.activeReport.name = action.payload.data.name;
				state.activeReport.type = action.payload.data.reportType;
				state.activeChartConfig = getValidChartType(action.payload.data.reportConfig);
			})
			.addCase(fetchReportById.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(deleteReport.pending, (state) => {
				state.error = null;
			})
			.addCase(deleteReport.fulfilled, (state, action) => {
				state.error = null;

				state.activeReport.reportId = action.payload.reportId;
				state.activeReport.name = action.payload.name;
				state.activeReport.type = action.payload.type;
				state.activeChartConfig = getValidChartType(action.payload.config);
			})
			.addCase(deleteReport.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(updateReportById.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateReportById.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.activeReport.name = action.payload;
			})
			.addCase(updateReportById.rejected, (state, action) => {
				state.error = action.payload as string;
				const previousReportDataBeforeUpdate =
					state.reportsMetaData.reports.reports.find(
						(report) =>
							report.reportId === state.activeReport.reportId,
					);
				state.activeChartConfig =
					previousReportDataBeforeUpdate?.config;
			})
			.addCase(exportReportAsCSV.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(exportReportAsCSV.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(exportReportAsPDF.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(exportReportAsPDF.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			});
	},
});

export const {
	resetReportingState,
	setGraphType,
	updateChartConfig,
	updatePage,
	updatePageSize,
	updateActiveReport,
	pushToast,
} = reportingSlice.actions;
export default reportingSlice.reducer;
